import type {
  OrchestrationMessage,
  OrchestrationLatestTurn,
  OrchestrationSessionStatus,
} from "@t3tools/contracts";

export type TimelineMessage = Pick<
  OrchestrationMessage,
  "id" | "role" | "text" | "phase" | "createdAt" | "sequence"
> & {
  turnId?: string | null | undefined;
  attachments?: ReadonlyArray<{ name: string }> | undefined;
};
export interface TimelineWork {
  id: string;
  createdAt: string;
  sequence?: number;
  turnId?: string | null;
}
export type TimelineEntry<
  M extends TimelineMessage = TimelineMessage,
  W extends TimelineWork = TimelineWork,
> =
  | { kind: "message"; id: string; createdAt: string; sequence?: number; message: M }
  | { kind: "work"; id: string; createdAt: string; sequence?: number; entry: W };
type MessageTimelineEntry = Extract<TimelineEntry, { kind: "message" }>;
function isWorkEntry(entry: TimelineEntry): boolean {
  return entry.kind === "work";
}
function isAssistantEntry(entry: TimelineEntry): entry is MessageTimelineEntry {
  return entry.kind === "message" && entry.message.role === "assistant";
}

export type LatestTurnTiming = Pick<
  OrchestrationLatestTurn,
  "turnId" | "startedAt" | "completedAt"
>;
export type SessionActivityState = {
  orchestrationStatus: OrchestrationSessionStatus;
  activeTurnId?: string | null | undefined;
};

export function isLatestTurnSettled(
  latestTurn: LatestTurnTiming | null,
  session: SessionActivityState | null,
): boolean {
  if (!latestTurn?.startedAt) return false;
  if (!latestTurn.completedAt) return false;
  if (!session) return true;
  if (session.orchestrationStatus === "running") return false;
  return true;
}

/**
 * One user turn: the user message that started it plus everything the agent
 * produced before the next user message. Items that carry a different turn id
 * (windowed history cut mid-turn, a turn that ended without a user message)
 * open a new segment so one segment never mixes two turns.
 */
export interface TurnSegment<T extends TimelineEntry = TimelineEntry> {
  userEntry: Extract<T, { kind: "message" }> | null;
  turnId: string | null;
  entries: T[];
}

function entryTurnId(entry: TimelineEntry): string | null {
  switch (entry.kind) {
    case "message":
      return entry.message.turnId ?? null;
    case "work":
      return entry.entry.turnId ?? null;
  }
}

export function splitTurnSegments<T extends TimelineEntry>(
  timelineEntries: ReadonlyArray<T>,
): TurnSegment<T>[] {
  const segments: TurnSegment<T>[] = [];
  let current: TurnSegment<T> | null = null;
  for (const entry of timelineEntries) {
    if (entry.kind === "message" && entry.message.role === "user") {
      current = { userEntry: entry as Extract<T, { kind: "message" }>, turnId: null, entries: [] };
      segments.push(current);
      continue;
    }
    const turnId = entryTurnId(entry);
    if (
      current === null ||
      (turnId !== null && current.turnId !== null && turnId !== current.turnId)
    ) {
      current = { userEntry: null, turnId: null, entries: [] };
      segments.push(current);
    }
    if (turnId !== null && current.turnId === null) {
      current.turnId = turnId;
    }
    current.entries.push(entry);
  }
  return segments;
}

function findTerminalAssistantIndex(entries: ReadonlyArray<TimelineEntry>): number {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    if (entry && isAssistantEntry(entry)) {
      return index;
    }
  }
  return -1;
}

/**
 * The turn's answer is the assistant message rendered outside the work group.
 * Messages that carry a phase decide it themselves: a question the agent asked
 * without pausing stays in the group; without a phase the last assistant
 * message is the answer once the turn settled, and while the turn is live it
 * stays the answer only until work follows it.
 */
export function collectAnswerMessageIds(
  entries: ReadonlyArray<TimelineEntry>,
  live: boolean,
): Set<string> {
  const answerIds = new Set<string>();
  const terminalIndex = findTerminalAssistantIndex(entries);
  const workAfterTerminal =
    terminalIndex !== -1 && entries.slice(terminalIndex + 1).some(isWorkEntry);
  entries.forEach((entry, index) => {
    if (!isAssistantEntry(entry)) return;
    const { message } = entry;
    if (message.phase === "final_answer") {
      answerIds.add(message.id);
      return;
    }
    if (message.phase === "commentary" || message.phase === "question") return;
    if (index === terminalIndex && (!live || !workAfterTerminal)) {
      answerIds.add(message.id);
    }
  });
  return answerIds;
}

export interface TimelineOrderKey {
  id: string;
  createdAt: string;
  sequence?: number | undefined;
}

export function compareTimelineEntries(left: TimelineOrderKey, right: TimelineOrderKey): number {
  if (left.sequence !== undefined && right.sequence !== undefined) {
    if (left.sequence !== right.sequence) {
      return left.sequence - right.sequence;
    }
  } else if (left.sequence !== undefined) {
    return 1;
  } else if (right.sequence !== undefined) {
    return -1;
  }

  return left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id);
}

export function deriveTimelineEntries<M extends TimelineMessage, W extends TimelineWork>(
  messages: ReadonlyArray<M>,
  workEntries: ReadonlyArray<W>,
): TimelineEntry<M, W>[] {
  const messageRows: TimelineEntry<M, W>[] = messages.map((message) => ({
    id: message.id,
    kind: "message",
    createdAt: message.createdAt,
    ...(message.sequence !== undefined ? { sequence: message.sequence } : {}),
    message,
  }));
  const workRows: TimelineEntry<M, W>[] = workEntries.map((entry) => ({
    id: entry.id,
    kind: "work",
    createdAt: entry.createdAt,
    ...(entry.sequence !== undefined ? { sequence: entry.sequence } : {}),
    entry,
  }));
  return [...messageRows, ...workRows].toSorted(compareTimelineEntries);
}
